import { useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Sidebar, type NavId } from "./Sidebar";
import { Topbar, type TopbarMode } from "./Topbar";
import { TypesModal } from "./TypesModal";
import { TaskTypesModal } from "./TaskTypesModal";
import { launchesApi } from "../api/endpoints";
import { useSettings } from "../hooks/useSettings";
import { useSession } from "../hooks/useSession";
import { useTaskEvents } from "../hooks/useTaskEvents";

interface AppLayoutProps {
  children: ReactNode;
}

const NAV_FROM_PATH = (pathname: string): NavId => {
  if (pathname.startsWith("/board")) return "board";
  if (pathname.startsWith("/tasks")) return "tasks";
  if (pathname.startsWith("/landings")) return "landings";
  if (pathname.startsWith("/templates")) return "templates";
  if (pathname.startsWith("/launch-types")) return "launchTypes";
  if (pathname.startsWith("/admin/tasks-audit")) return "tasksAudit";
  if (pathname.startsWith("/admin/roles")) return "adminRoles";
  if (pathname.startsWith("/settings")) return "settings";
  if (pathname.startsWith("/admin")) return "admin";
  return "launches";
};

const TOPBAR_MODE: Record<NavId, TopbarMode> = {
  launches: "launches",
  board: "board",
  tasks: "tasks",
  templates: "templates",
  launchTypes: "templates",
  landings: "landings",
  tags: "tags",
  taskTypes: "tasks",
  admin: "launches",
  adminRoles: "launches",
  tasksAudit: "tasks",
  settings: "launches",
};

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { settings, update } = useSettings();

  const [typesOpen, setTypesOpen] = useState(false);
  const [taskTypesOpen, setTaskTypesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  const sessionQuery = useSession();
  const me = sessionQuery.data ?? null;
  const canViewLaunches = me?.permissions?.launches?.view === true;
  const canViewTasks =
    me?.permissions?.tasks?.view === true || me?.permissions?.tasks?.viewOwn === true;
  useTaskEvents(canViewTasks);

  // Список запусков нужен сайдбару (быстрый доступ к активным) и Topbar.
  // Без права launches.view бэк вернёт 403 на каждый рендер — отключаем
  // запрос для таких ролей, чтобы не плодить ошибочных fetch'ей.
  const launchesQuery = useQuery({
    queryKey: ["launches"],
    queryFn: async () => (await launchesApi.list()).launches,
    enabled: canViewLaunches,
  });
  const launches = launchesQuery.data ?? [];

  const collapsed = settings?.sidebarCollapsed ?? false;
  const activeLaunchId = settings?.activeLaunchId ?? null;
  const activeNav = NAV_FROM_PATH(location.pathname);

  const goNav = (id: NavId) => {
    if (id === "launches") navigate("/launches");
    else if (id === "board") {
      if (activeLaunchId) navigate("/board");
      else navigate("/launches");
    } else if (id === "tasks") navigate("/tasks");
    else if (id === "templates") navigate("/templates");
    else if (id === "launchTypes") navigate("/launch-types");
    else if (id === "landings") navigate("/landings");
    else if (id === "admin") navigate("/admin");
    else if (id === "adminRoles") navigate("/admin/roles");
    else if (id === "tasksAudit") navigate("/admin/tasks-audit");
    else if (id === "settings") navigate("/settings");
    else if (id === "tags") setTypesOpen(true);
    else if (id === "taskTypes") setTaskTypesOpen(true);
  };

  const pickLaunch = (id: string) => {
    update({ activeLaunchId: id });
    navigate("/board");
  };

  return (
    <div
      className={
        "app" +
        (collapsed ? " sidebar-collapsed" : "") +
        (mobileMenuOpen ? " mobile-menu-open" : "")
      }
    >
      <Sidebar
        activeNav={activeNav}
        activeLaunchId={activeLaunchId}
        collapsed={collapsed}
        mobileOpen={mobileMenuOpen}
        launches={launches}
        me={me}
        onNav={goNav}
        onPickLaunch={pickLaunch}
        onNewLaunch={() => navigate("/launches?new=1")}
        onToggleCollapse={() => update({ sidebarCollapsed: !collapsed })}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      {mobileMenuOpen && (
        <div
          className="mobile-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
      <div className="main">
        <Topbar
          mode={TOPBAR_MODE[activeNav]}
          launches={launches}
          activeLaunchId={activeLaunchId}
          canViewTasks={canViewTasks}
          onAllLaunches={() => navigate("/launches")}
          onMobileMenu={() => setMobileMenuOpen(true)}
        />
        {children}
      </div>
      {typesOpen && <TypesModal onClose={() => setTypesOpen(false)} />}
      {taskTypesOpen && <TaskTypesModal onClose={() => setTaskTypesOpen(false)} />}
    </div>
  );
}
